import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import {
  ApiReportTreatmentPlanAiTargetPredictionStatusChoices,
  ReportGoalType,
  ReportTargetTypeEdge,
  useCreateUpdateReportTargetMutation,
  useDuplicateReportGoalMutation,
  useGetTargetsQuery,
  useRemoveReportGoalMutation,
} from "@api/graphql/types-and-hooks";
import { AddTargetsBank } from "@components/GoalsBank/AddTargetsBank/AddTargetsBankV2";
import { MenuItem, MenuSeparator } from "@components/Menu";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useIsGasEnabled } from "@hooks/useIsGasEnabled";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReportProgrammingData } from "@providers/ReportProgrammingProvider";
import { useReport } from "@providers/ReportProvider";

interface GoalActionsMenuProps {
  close: () => void;
  goal: ReportGoalType;
}

export const GoalActionsMenu: React.FC<GoalActionsMenuProps> = ({ close, goal }) => {
  const go = useNavigate();
  const { isAddTargetsEnabled } = useClinicalReportDetailsData();
  const { report } = useReport();
  const queryClient = useQueryClient();
  const { mutate: removeReportGoalMutation } = useRemoveReportGoalMutation({
    onSuccess: () => {
      notifySuccess("Goal removed successfully");
      queryClient.invalidateQueries({
        queryKey: ["get-reassessment-goals"],
        exact: false,
      });
    },
    onError: () => {
      notifyError("Failed to remove goal");
    },
  });
  const { mutate: duplicateReportGoalMutation } = useDuplicateReportGoalMutation({
    onSuccess: () => {
      notifySuccess("Goal duplicated successfully");
      queryClient.invalidateQueries({
        queryKey: ["get-reassessment-goals"],
        exact: false,
      });
    },
    onError: () => {
      notifyError("Failed to duplicate goal");
    },
  });
  const isGasEnabled = useIsGasEnabled();

  const reportTreatmentPlan = report?.reportTreatmentPlan;
  const aiTargetPredictionStatus = reportTreatmentPlan?.aiTargetPredictionStatus;

  const isAiTargetsFailed = aiTargetPredictionStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.Failure;
  const isAiTargetsLoading =
    aiTargetPredictionStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.Pending ||
    aiTargetPredictionStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPending;

  const { reassessmentGoalsQuery, selected, dispatchSelected } = useReportProgrammingData();
  const { mutateAsync: createReportTarget } = useCreateUpdateReportTargetMutation({});

  const goalIdForTargets = goal.id;
  const { data: targets, refetch: refetchTargets } = useGetTargetsQuery(
    {
      filter: {
        goalId: goalIdForTargets ?? "<missing-goal-id>",
        learnerId: report?.learner?.id ?? "<missing-learner-id>",
      },
    },
    {
      queryKey: ["get-targets", goalIdForTargets, report?.learner?.id],
      enabled: !!goalIdForTargets && !!report?.learner?.id,
    },
  );

  // When polling detects AI target generation is complete, refetch targets so
  // useGetTargetsQuery (independent of the polled report query) gets the new AI targets.
  const prevAiTargetPredictionStatusRef = useRef(aiTargetPredictionStatus);
  useEffect(() => {
    const prev = prevAiTargetPredictionStatusRef.current;
    prevAiTargetPredictionStatusRef.current = aiTargetPredictionStatus;
    const wasLoading =
      prev === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.Pending ||
      prev === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPending;
    const isNowPredicted =
      aiTargetPredictionStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionPredicted ||
      aiTargetPredictionStatus === ApiReportTreatmentPlanAiTargetPredictionStatusChoices.GoalPredictionSkipped;
    if (wasLoading && isNowPredicted && goalIdForTargets && report?.learner?.id && !isGasEnabled) {
      refetchTargets();
    }
  }, [aiTargetPredictionStatus, goalIdForTargets, report?.learner?.id, isGasEnabled]);

  const targetEdges = (targets?.getReportTargets?.edges.filter((edge) => !!edge?.node) || []) as ReportTargetTypeEdge[];
  const aiTargetEdges = targetEdges.filter((target) => target?.node?.aiGenerated && !target?.node?.inUse);
  const nonAiTargetEdges = targetEdges.filter((target) => !target?.node?.aiGenerated);

  const handleAddCustomTarget = async () => {
    try {
      const response = await createReportTarget({
        targetData: {
          reportGoalId: goal.id,
          reportId: report?.id ?? "<missing-report-id>",
        },
        learnerId: report?.learner?.id ?? "<missing-learner-id>",
      });
      if (!response.createUpdateReportTarget?.status) {
        notifyError(response.createUpdateReportTarget?.message || "Failed to create custom target");
        return;
      }
      // Backend seeds template defaults on create (matching IA). Refetch, then
      // open the new target's editor.
      await refetchTargets();
      await reassessmentGoalsQuery.refetch();
      const createdId = response.createUpdateReportTarget?.reportTargetDetails?.id;
      if (createdId) {
        dispatchSelected({ type: "SELECT_TARGET", targetId: createdId, goalId: goal.id });
      }
    } catch (err) {
      console.error("Error creating custom target", err);
      notifyError("Failed to create custom target");
    }
  };

  const handleManageEhrTargets = () => {
    go("../select-programming", {
      state: { returnToReportPage: true, domainId: goal?.reportDomain?.id, scrollToGoalId: goal?.goal?.id },
    });
  };

  const handleAddSuggestedTarget = () => {
    dispatchSelected({ type: "SELECT_GOAL", goalId: goal.id });
  };

  const handleRemoveGoalFromReport = () => {
    removeReportGoalMutation({ reportGoalId: goal.id });
    // Clear selection if the removed goal was selected
    if (selected.mode === "goal" && selected.goalId === goal.id) {
      dispatchSelected({ type: "CLEAR" });
    }
  };

  const handleDuplicateGoal = () => {
    duplicateReportGoalMutation({
      learnerId: report?.learner?.id ?? "<missing-learner-id>",
      reportGoalId: goal.id,
    });
  };

  return (
    <>
      {isAddTargetsEnabled && (
        <>
          <MenuItem
            onClick={() => {
              void handleAddCustomTarget();
              close();
            }}
          >
            Custom Target
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleManageEhrTargets();
              close();
            }}
          >
            Manage EHR targets
          </MenuItem>
          <AddTargetsBank
            key={goal?.id}
            learnerId={report?.learner?.id ?? "<missing-learner-id>"}
            reportGoalId={goal?.id ?? ""}
            shortTermGoalBankId={goal?.goalBank?.id}
            assessmentId={report?.id ?? "<missing-assessment-id>"}
            onAdd={async () => {
              const goalWeAddedTo = goal;
              const previousTargetIds = new Set(targetEdges.map((e) => e?.node?.id).filter((id): id is string => !!id));
              const { data } = await refetchTargets();
              await reassessmentGoalsQuery.refetch();
              const edges = (data?.getReportTargets?.edges?.filter((e) => !!e?.node) ?? []) as ReportTargetTypeEdge[];
              const newlyAddedNode = edges.find((e) => e?.node?.id && !previousTargetIds.has(e.node.id))?.node;
              if (newlyAddedNode && goalWeAddedTo) {
                dispatchSelected({ type: "SELECT_TARGET", targetId: newlyAddedNode.id, goalId: goalWeAddedTo.id });
              }
            }}
            reportType={report?.reportType}
            targets={nonAiTargetEdges}
            aiTargets={aiTargetEdges}
            isAiTargetsFailed={isAiTargetsFailed}
            isAiTargetsLoading={isAiTargetsLoading}
            modalButton={
              <MenuItem
                onClick={() => {
                  handleAddSuggestedTarget();
                }}
              >
                Suggested Targets
              </MenuItem>
            }
          />
        </>
      )}
      {isAddTargetsEnabled && <MenuSeparator />}
      <MenuItem
        onClick={() => {
          handleDuplicateGoal();
          close();
        }}
      >
        Duplicate goal
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleRemoveGoalFromReport();
          close();
        }}
      >
        Remove goal from report
      </MenuItem>
    </>
  );
};
